import type {Icon} from '@primer/octicons-react'
import {
  CommentDiscussionIcon,
  CopyIcon,
  DeviceDesktopIcon,
  GitPullRequestIcon,
  ArchiveIcon,
  LockIcon,
  PackageIcon,
  PencilIcon,
  TrashIcon,
  UnlockIcon,
} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'
import {copyText} from '@github-ui/copy-to-clipboard'
import {sendEvent} from '@github-ui/hydro-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isCopilotCLICompatibleAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {SessionState} from '../types/session'
import type {Pull} from '../types/pull'
import {extractSpecTaskThreadIdFromEventIdentifiers} from '../utils/extract-spec-task-thread-id'
import {COPILOT_CHAT_SPEC_TASK_EVENT_TYPE} from '../utils/constants'
import {generateTaskPathForRepo} from '../utils/generate-task-path'
import {copilotAppTaskUrl} from '../utils/url-helpers'
import {useArchiveDialogOptional} from '../contexts/ArchiveDialogContext'
import {useDeleteTaskDialogOptional} from '../contexts/DeleteTaskDialogContext'
import {useTaskSharingDialogOptional} from '../contexts/TaskSharingDialogContext'
import {useUnarchiveTask} from './use-unarchive-task'
import {useArchiveTask} from './use-archive-task'
import {isQuickChatTaskViewEnabled} from '../utils/is-quick-chat-task'
import {canChangeTaskSharing} from '../utils/task-sharing'
import {useTaskPinAction} from './use-task-pin-action'

export interface TaskActionItem {
  key: string
  icon: Icon
  label: string
  href?: string
  variant?: 'danger'
  disabled?: boolean
  loading?: boolean
  onSelect: (event?: React.SyntheticEvent) => void
}

export interface UseTaskActionItemsProps {
  state?: string
  taskId?: string
  pull?: Pull
  repoOwner?: string
  repoName?: string
  currentUserIsOwner?: boolean
  archivedAt?: string | null
  eventType?: string | null
  eventIdentifiers?: string[] | null
  hideCopy?: boolean
  hidePull?: boolean
  hideArchive?: boolean
  hideShare?: boolean
  hideDelete?: boolean
  interactiveSession?: boolean
  agentSlug?: string
  isQuickChatTask?: boolean
  sharingStatus?: 'unshared' | 'shared'
  onRename?: () => void
  automationId?: string | null
  enablePinning?: boolean
  /**
   * When the sharing action is surfaced in a menu (e.g. the narrow-viewport
   * title menu), the triggering item unmounts as the dialog opens. Pass the
   * menu trigger here so focus returns to a stable target on dialog close.
   */
  shareReturnFocusRef?: React.RefObject<HTMLElement | null>
}

export function isSessionCompleted(state?: string): boolean {
  if (!state) return false
  return [SessionState.Completed, SessionState.Failed, SessionState.Cancelled].map(s => s.toLowerCase()).includes(state)
}

export function canUnarchive(props: UseTaskActionItemsProps): boolean {
  const {archivedAt, pull, taskId, currentUserIsOwner, interactiveSession, hideArchive} = props
  if (hideArchive) return false
  if (!archivedAt || !taskId) return false
  if (interactiveSession) return true
  return !!(pull?.state !== 'merged' && currentUserIsOwner)
}

export function canArchive(props: UseTaskActionItemsProps): boolean {
  const {state, taskId, currentUserIsOwner, archivedAt, interactiveSession, hideArchive} = props
  if (hideArchive) return false
  return !!((isSessionCompleted(state) || interactiveSession) && !archivedAt && taskId && currentUserIsOwner)
}

export function hasConversationAction(props: Pick<UseTaskActionItemsProps, 'eventType' | 'eventIdentifiers'>): boolean {
  const isTaskPlanLinksEnabled = isFeatureEnabled('copilot_immersive_task_hyperlinking')
  if (props.eventType !== COPILOT_CHAT_SPEC_TASK_EVENT_TYPE || !isTaskPlanLinksEnabled) return false
  const threadId = extractSpecTaskThreadIdFromEventIdentifiers(props.eventIdentifiers)
  return !!threadId
}

export function useTaskActionItems(props: UseTaskActionItemsProps): {
  items: TaskActionItem[]
  hasAnyAction: boolean
  pinError?: string
  dismissPinError: () => void
} {
  const {
    taskId,
    pull,
    repoOwner,
    repoName,
    currentUserIsOwner,
    eventType,
    eventIdentifiers,
    hideCopy = false,
    hidePull = false,
    hideShare = false,
    hideDelete = false,
    agentSlug,
    isQuickChatTask = false,
    sharingStatus,
    onRename,
    automationId,
    shareReturnFocusRef,
  } = props
  const pin = useTaskPinAction({
    taskId,
    enabled: props.enablePinning,
  })

  const archiveDialog = useArchiveDialogOptional()
  const deleteDialog = useDeleteTaskDialogOptional()
  const sharingDialog = useTaskSharingDialogOptional()
  const {mutate: unarchiveTask} = useUnarchiveTask({
    taskId,
    repoNwo: repoOwner && repoName ? `${repoOwner}/${repoName}` : undefined,
  })
  const {mutate: archiveTask} = useArchiveTask({
    taskId,
    repoOwner,
    repoName,
  })

  const conversationThreadId = extractSpecTaskThreadIdFromEventIdentifiers(eventIdentifiers)
  const conversationHref = conversationThreadId ? `/copilot/c/${conversationThreadId}` : undefined
  const showConversation = hasConversationAction({eventType, eventIdentifiers})
  const quickChatUsesTaskView = isQuickChatTask && isQuickChatTaskViewEnabled()
  const quickChatMutationHasApiUrl = !quickChatUsesTaskView || !!archiveDialog?.chatApiUrl
  const showArchive = canArchive(props) && quickChatMutationHasApiUrl
  const showUnarchive = canUnarchive(props) && quickChatMutationHasApiUrl

  const handleCopyLink = useCallback(
    async (event?: React.SyntheticEvent) => {
      try {
        const path = generateTaskPathForRepo({
          repositoryNwo: repoOwner && repoName ? `${repoOwner}/${repoName}` : undefined,
          taskId,
        })
        const fullUrl = `${window.location.origin}${path}`
        await copyText(fullUrl)
        sendEvent('agent_sessions.task_actions', {
          target: 'COPY_TASK_LINK',
          task_id: taskId ?? pull?.global_relay_id,
        })
      } catch {
        event?.preventDefault()
        event?.stopPropagation()
      }
    },
    [repoOwner, repoName, taskId, pull],
  )

  const handleArchive = useCallback(() => {
    if (!taskId) return

    if (pull?.state === 'open' && archiveDialog) {
      archiveDialog.openArchiveDialog(taskId, repoOwner, repoName, pull)
    } else {
      archiveTask()
    }
  }, [taskId, repoOwner, repoName, pull, archiveDialog, archiveTask])

  const handleUnarchive = useCallback(() => {
    unarchiveTask()
  }, [unarchiveTask])

  const handleShareSession = useCallback(() => {
    if (!taskId || !sharingDialog) return
    sharingDialog.openSharingDialog(taskId, repoOwner, repoName, false, shareReturnFocusRef)
  }, [taskId, repoOwner, repoName, sharingDialog, shareReturnFocusRef])

  const handleUnshareSession = useCallback(() => {
    if (!taskId || !sharingDialog) return
    sharingDialog.openSharingDialog(taskId, repoOwner, repoName, true, shareReturnFocusRef)
  }, [taskId, repoOwner, repoName, sharingDialog, shareReturnFocusRef])

  const handleDeleteSession = useCallback(() => {
    if (!taskId || !deleteDialog) return
    deleteDialog.openDeleteDialog(taskId, repoOwner, repoName)
  }, [taskId, repoOwner, repoName, deleteDialog])

  const items = useMemo(() => {
    const result: TaskActionItem[] = []
    if (pin.action) result.push(pin.action)

    if (onRename && taskId && currentUserIsOwner && quickChatMutationHasApiUrl) {
      result.push({
        key: 'rename',
        icon: PencilIcon,
        label: 'Rename',
        onSelect: onRename,
      })
    }

    if (
      sharingDialog &&
      canChangeTaskSharing({
        taskId,
        currentUserIsOwner: Boolean(currentUserIsOwner),
        hasRepository: Boolean(repoOwner && repoName),
        supportsSharing: !hideShare,
      })
    ) {
      const isShared = sharingStatus === 'shared'
      result.push({
        key: 'sharing-settings',
        icon: isShared ? UnlockIcon : LockIcon,
        label: 'Sharing settings',
        onSelect: isShared ? handleUnshareSession : handleShareSession,
      })
    }

    if (!hideCopy && ((repoOwner && repoName && (taskId || pull?.number)) || taskId)) {
      result.push({
        key: 'copy-link',
        icon: CopyIcon,
        label: 'Copy link',
        onSelect: handleCopyLink,
      })
    }

    if (!hidePull && pull?.url) {
      result.push({
        key: 'view-pr',
        icon: GitPullRequestIcon,
        label: 'View pull request',
        href: pull.url,
        onSelect: () => {
          sendEvent('dotcom_chat.activate', {
            target: 'TASK_ACTION_VIEW_PR',
            mode: 'immersive',
          })
        },
      })
    }

    if (showConversation && conversationHref) {
      result.push({
        key: 'go-to-conversation',
        icon: CommentDiscussionIcon,
        label: 'Go to parent conversation',
        href: conversationHref,
        onSelect: () => {
          sendEvent('dotcom_chat.activate', {
            target: 'TASK_ACTION_GO_TO_CONVERSATION',
            mode: 'immersive',
          })
        },
      })
    }

    if (automationId && taskId) {
      result.push({
        key: 'open-in-copilot-app',
        icon: DeviceDesktopIcon,
        label: 'Open in GitHub Copilot App',
        href: copilotAppTaskUrl(automationId, taskId),
        onSelect: () => {
          sendEvent('agent_sessions.task_actions', {
            target: 'OPEN_IN_COPILOT_APP',
            task_id: taskId,
          })
        },
      })
    }

    if (showArchive) {
      result.push({
        key: 'archive-session',
        icon: ArchiveIcon,
        label: 'Archive session',
        onSelect: handleArchive,
      })
    }

    if (showUnarchive) {
      result.push({
        key: 'unarchive-session',
        icon: PackageIcon,
        label: 'Unarchive session',
        onSelect: handleUnarchive,
      })
    }

    if (
      !hideDelete &&
      deleteDialog &&
      taskId &&
      currentUserIsOwner &&
      (isCopilotCLICompatibleAgent(agentSlug) || (quickChatUsesTaskView && !!deleteDialog.chatApiUrl))
    ) {
      result.push({
        key: 'delete-session',
        icon: TrashIcon,
        label: 'Delete',
        variant: 'danger',
        onSelect: handleDeleteSession,
      })
    }

    return result
  }, [
    hideCopy,
    hidePull,
    hideShare,
    hideDelete,
    sharingStatus,
    repoOwner,
    repoName,
    pull,
    taskId,
    automationId,
    showConversation,
    conversationHref,
    agentSlug,
    quickChatUsesTaskView,
    currentUserIsOwner,
    showArchive,
    showUnarchive,
    quickChatMutationHasApiUrl,
    handleCopyLink,
    handleArchive,
    handleUnarchive,
    handleShareSession,
    handleUnshareSession,
    handleDeleteSession,
    sharingDialog,
    deleteDialog,
    onRename,
    pin.action,
  ])

  return {items, hasAnyAction: items.length > 0, pinError: pin.error, dismissPinError: pin.dismissError}
}
